import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthImage from "../images/auth-image.png";
import { Button } from "../partials/actions/Button";
import logo from "../images/landingPage/logo.svg";
import { auth, firestore } from "../firebaseConfig";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { useToast } from "../components/CustomToast";
import { loadStripe } from "@stripe/stripe-js";

const stripePromise = loadStripe('pk_live_51Okws8BEGs3qjAhIraHeYYuMLY2MjTf46yDLHzadAKF1eSuveCFdR2wDzLpFKgpkfE9gLCXlzTDRdyOzvWrT13IR00vS9A51PU');

function Signup() {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("basic"); 
  const navigate = useNavigate();
  const { addToast } = useToast();

  const createFirestoreDocument = async (user) => {
    try {
      if (!fullName || !user.email || !user.uid) {
        throw new Error("Missing required user data.");
      }
      const userDoc = {
        fullName,
        email: user.email,
        emailVerified: false,
        uid: user.uid,
        createdAt: new Date().toISOString(),
        subscription: selectedPlan
      };
      await setDoc(doc(firestore, "users", user.uid), userDoc);
      return true;
    } catch (error) {
      console.error("Error creating Firestore document:", error);
      throw error;
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setIsLoading(true);
  
    try {
      const signupData = {
        email,
        password,
        fullName,
        selectedPlan
      };
      localStorage.setItem('pendingSignup', JSON.stringify(signupData));
  
      const response = await fetch('https://investorverden.onrender.com/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          fullName,
          productType: selectedPlan
        }),
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const { id: sessionId } = await response.json();
      const stripe = await stripePromise;
      const { error } = await stripe.redirectToCheckout({ sessionId });
  
      if (error) throw error;
    } catch (error) {
      console.error("Signup Error:", error);
      addToast("Failed to create checkout session", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="bg-black dark:bg-gray-900">
      <div className="relative md:flex">
        <div className="md:w-1/2">
          <div className="min-h-[100dvh] h-full flex flex-col after:flex-1">
            <div className="flex-1">
              <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
                <Link className="block" to="/">
                  <img src={logo} alt="Logo" className="h-20 w-auto" />
                </Link>
              </div>
            </div>

            <div className="max-w-sm mx-auto w-full px-4 py-8">
              <h1 className="text-3xl text-white font-bold mb-6">Opret din konto</h1>
              <form onSubmit={handleSignup}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1" htmlFor="plan">
                      Vælg abonnement <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="plan"
                      className="form-select w-full"
                      value={selectedPlan}
                      onChange={(e) => setSelectedPlan(e.target.value)}
                      disabled={isLoading}
                    >
                      <option value="basic">Årligt abonnement - 1800 DKK/året</option>
                      <option value="premium">Månedlig abonnement - 180 DKK/måneden</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" htmlFor="fullName">
                      Fulde navn <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="fullName"
                      className="form-input w-full"
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" htmlFor="email">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="email"
                      className="form-input w-full"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" htmlFor="password">
                      Kodeord <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="password"
                      className="form-input w-full"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={isLoading}
                    />
                  </div>
                  {/* Terms & Policy Link */}
                  <div className="mt-2 text-sm text-gray-300 hover:text-gray-200">
                    <Link to="/terms-and-policy">Læs vilkår & betingelser</Link>
                  </div>
                  <div>
                    <Button
                      type="submit"
                      text={isLoading ? "Loader..." : "Fortsæt til betaling"}
                      variant="primary"
                      className="w-full rounded-md mt-4"
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </form>
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Har du allerede en konto?{" "}
                  <Link to="/signin" className="text-violet-500 hover:underline">
                    Log ind
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="hidden md:flex md:w-1/2 bg-gray-100 h-screen overflow-auto flex-col justify-between">
          <div className="p-8 text-right text-black">
            <p className="text-lg">
              "Someone's sitting in the shade today,<br /> because someone planted a tree a long time ago."
            </p>
            <p className="text-sm mt-2">- Warren Buffett</p>
          </div>
          <div className="relative flex justify-end items-end h-full">
            <img className="object-contain w-2/3 h-auto" src={AuthImage} alt="Authentication" />
          </div>
        </div>
      </div>
    </main>
  );
}

export default Signup;